/**
 * An enum representing the role of the user in zenity
 */
export enum ZenityUserRoleEnum {
  viewer = 'viewer',
  operator = 'operator',
  analyst = 'analyst',
  admin = 'admin',
}

export interface UserAccessGroup {
  id: string;
  name: string;
}

export interface AccountManagementUserModel {
  userId: string;
  email: string;
  name: string;
  createdAt: Date;
  lastLogin: Date | null;
  role?: ZenityUserRoleEnum;
  pictureUrl: URL;
  accessGroups?: UserAccessGroup[];
}

export interface UserToAddModel {
  email: string;
  role: ZenityUserRoleEnum;
  authentication_id: string;
}

export interface ModifyUserRoleModel {
  userId: string;
  newRole: ZenityUserRoleEnum;
}

export interface AccountManagementClientApiModel {
  clientId: string;
  clientSecret: string;
}

export interface AccountManagementAuthenticationModel {
  id: string;
  name: string;
  strategy?: string;
}

export interface AccountManagementInvitationModel {
  id: string;
  email: string;
  name: string;
  createdAt: Date;
  expiresAt: Date;
  role: ZenityUserRoleEnum;
  invitationUrl: string;
}

export enum UsersSupportedActions {
  Delete = 'delete',
  Copy = 'copy',
  Edit = 'edit',
}

export enum AccountCategory {
  paid = 'paid',
  zenity = 'zenity',
  internal = 'internal',
  poc = 'poc', // limited access to the platform
  inactive = 'inactive', // when user tested "POC" and then canceled the subscription. Handled on BE side
}

export interface AccountCategoryResponse {
  id: string;
  category: AccountCategory;
}

export interface AuditLogEventModel {
  id: string;
  timestamp: Date;
  objectType: AuditLogObjectType;
  objectId: string;
  action: AuditLogActionType;
  actorType: AuditLogActorType;
  actorName: string;
  details?: string;
}

export enum AuditLogActionType {
  Create = 'Create',
  Edit = 'Edit',
  Delete = 'Delete',
  Exempt = 'Exempt',
  RemoveAccess = 'Remove access',
  Login = 'Login',
  SetAgentAuthentication = 'Set agent authentication',
  UpdateViolations = 'UpdateViolations',
  Execute = 'Execute',
  Export = 'Export',
  Import = 'Import',
  Archive = 'Archive',
  Restore = 'Restore',
}

export enum AuditLogActorType {
  User = 'User',
  Automatic = 'Automatic',
  API = 'API',
  System = 'System',
}

export enum AuditLogObjectType {
  Integration = 'Integration',
  Policy = 'Policy',
  Violation = 'Violation',
  Playbook = 'Playbook',
  User = 'User',
  Rule = 'Rule',
}

export interface AuditLogEventApiResponse {
  id: string;
  timestamp: string;
  entityType: string;
  entityAction: string;
  actor: {
    email: string;
    source: string;
  };
  metadata?: {
    entity_id?: string;
    entity_parent_id?: string;
    entity_parent_type?: string;
  };
  _links?: unknown;
}

export interface AuditLogEventRowData {
  id: string;
  timestamp: Date;
  objectType: string;
  objectId: string;
  action: string;
  actorType: string;
  actorEmail: string[];
  details?: string;
  detailsLink?: string;
  iconSrc?: string;
  objectTypeIconClass?: string;
  rowId: string;
  clickable: boolean;
  fullEvent?: AuditLogEventApiResponse;
}

export interface AccessGroupRowViewModel {
  id: string;
  name: string;
  members: number;
  createdBy: string[];
  createdAt: Date;
  lastChanged: Date;
  users: string | string[];
  role: string;
  roleId: string;
  integrations: string;
  integrationIds: string[];
  allIntegrations: boolean;
  clickable: boolean;
  rowId: string;
}

export interface AccessControlEntry {
  integrationId: string;
}

export interface AccessGroupApiResponse {
  id: string;
  name: string;
  roleId: string;
  users: string[];
  accessControls: AccessControlEntry[];
  allIntegrations?: boolean;
  createdTime: string;
  updatedTime: string;
  updater: string;
  _links: unknown;
}

export interface CreateAccessGroupRequest {
  name: string;
  roleId: string;
  users: string[];
  accessControls: AccessControlEntry[];
  allIntegrations: boolean;
}

export interface UpdateAccessGroupRequest {
  name?: string;
  roleId?: string;
  users?: string[];
  accessControls?: AccessControlEntry[];
  allIntegrations?: boolean;
}

export interface RoleApiResponse {
  id: string;
  name: string;
  description: string;
}

export interface FeatureFlagResponse {
  key: string;
  value: boolean;
}

export interface AccessPermissionEntry {
  hasAccessToAll: boolean;
  integrationIds: string[];
}

export type AccessPermissions = Record<string, AccessPermissionEntry>;

export interface UserDetailsResponse {
  featureFlags: FeatureFlagResponse[];
  roles: string[];
  permissions: string[];
  accessPermissions?: AccessPermissions;
}
